import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import MapPinPicker from "@/components/MapPinPicker";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { PriceDisplay } from "@/lib/plan";
import {
  Plus, Trash2, Pencil, Copy, Link as LinkIcon, Share2,
  MessageCircle, Smartphone, Mail, MapPin,
} from "lucide-react";

const empty = { name: "", slug: "", pin: "", active: true, email: "", base_address: "", base_lat: null, base_lng: null };

function StaffDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name || "",
        slug: initial.slug || "",
        pin: "",
        active: initial.active ?? true,
        email: initial.email || "",
        base_address: initial.base_address || "",
        base_lat: initial.base_lat ?? null,
        base_lng: initial.base_lng ?? null,
      });
    } else {
      setForm({ ...empty, base_address: "", base_lat: null, base_lng: null });
    }
  }, [initial, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!isEdit && !/^\d{4}$/.test(form.pin)) { toast.error("PIN must be 4 digits"); return; }
    if (isEdit && form.pin && !/^\d{4}$/.test(form.pin)) { toast.error("PIN must be 4 digits"); return; }
    setSubmitting(true);
    try {
      const payload = { name: form.name.trim(), active: form.active };
      if (form.slug?.trim()) payload.slug = form.slug.trim();
      if (form.pin) payload.pin = form.pin;
      // Phase 18: optional email for booking notifications. Empty string clears it on update.
      payload.email = (form.email || "").trim();
      // Phase 43: per-staff base/garage address. Empty string clears.
      payload.base_address = (form.base_address || "").trim();
      let savedStaff;
      if (isEdit) {
        const r = await api.put(`/staff/${initial.id}`, payload);
        savedStaff = r.data;
        toast.success("Staff updated");
      } else {
        const r = await api.post("/staff", payload);
        savedStaff = r.data;
        toast.success("Staff created");
      }
      // Phase 43 — surface geocode outcome inline so the manager knows whether
      // a follow-up map-pin is required.
      if (payload.base_address && !savedStaff?.base_lat) {
        toast.warning("Base address saved but not geocoded — drop a pin from the staff card to set exact coordinates.");
      }
      onSaved();
      onOpenChange(false);
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="staff-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit staff" : "New staff member"}</DialogTitle>
          <DialogDescription>
            Slug is used in their personal URL. PIN is hashed at rest.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="staff-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Slug</Label>
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated if blank" data-testid="staff-slug-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">{isEdit ? "New PIN (leave blank to keep)" : "PIN (4 digits) *"}</Label>
            <Input
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="1234"
              inputMode="numeric"
              maxLength={4}
              data-testid="staff-pin-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="staff-active-switch" />
            <Label className="text-sm">Active</Label>
          </div>
          {/* Phase 18: optional staff email for booking notifications */}
          <div>
            <Label className="text-xs uppercase mono tracking-widest text-muted-foreground">Email (optional)</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="staff@company.com" data-testid="staff-email-input" />
            <div className="text-[11px] text-muted-foreground mt-1">If set, this staff member will receive booking notifications by email.</div>
          </div>
          {/* Phase 43 — base / garage address (used for daily-mileage home legs). */}
          <div className="space-y-1">
            <Label className="text-xs uppercase mono tracking-widest text-muted-foreground">Base address (optional)</Label>
            <Textarea
              value={form.base_address || ""}
              onChange={(e) => setForm({ ...form, base_address: e.target.value })}
              rows={2}
              placeholder="e.g. Yellow Yard, Killarney Industrial Estate, V93 X9F7"
              data-testid="staff-base-address-input"
            />
            <div className="text-[11px] text-muted-foreground mt-1">
              Where this driver starts and ends their day. Used to compute daily mileage including the home→first-job and last-job→home legs.
            </div>
            {form.base_lat != null && form.base_lng != null && (
              <div className="text-[11px] text-emerald-600 font-semibold mt-1" data-testid="staff-base-geocoded-badge">
                ✓ Geocoded · {form.base_lat.toFixed(4)}, {form.base_lng.toFixed(4)}
              </div>
            )}
            {isEdit && (form.base_address || "").trim() && form.base_lat == null && (
              <div className="text-[11px] text-amber-600 font-semibold mt-1" data-testid="staff-base-not-geocoded">
                ⚠ Address not geocoded — drop a pin from the staff card.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="staff-cancel-button">Cancel</Button>
            <Button type="submit" disabled={submitting} className="btn-cta" data-testid="staff-save-button">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Staff() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 43 — open the MapPinPicker for a specific staff member to set
  // their base lat/lng without going through geocoding.
  const [pinningStaff, setPinningStaff] = useState(null);
  // Phase O — surface plan limits for the banner + button gating.
  const [planLimits, setPlanLimits] = useState(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    setLoading(true);
    try {
      const [staffRes, limitsRes] = await Promise.all([
        api.get("/staff"),
        api.get("/plan/limits").catch(() => ({ data: null })),
      ]);
      setItems(staffRes.data);
      setPlanLimits(limitsRes.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    try {
      await api.delete(`/staff/${id}`);
      toast.success("Staff deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Staff URL copied");
  };

  // Phase O — banner + button gating based on active-staff count vs cap.
  const activeCount = items.filter((s) => s.active).length;
  const maxStaff = planLimits?.limits?.max_staff ?? null;
  const atCap = maxStaff !== null && activeCount === maxStaff;
  const overCap = maxStaff !== null && activeCount > maxStaff;
  const limitBanner = overCap
    ? { tone: "red", text: `You're over your plan's staff limit (${activeCount}/${maxStaff}). New staff blocked until you upgrade or archive existing.` }
    : atCap
    ? { tone: "amber", text: `${planLimits?.plan_code || "Your"} plan limit reached: ${activeCount}/${maxStaff} staff. Upgrade to Pro to add more.` }
    : null;

  return (
    <div className="page" data-testid="staff-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Manage</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Staff</h1>
        </div>
        <Button
          className="btn-cta gap-2"
          onClick={() => { setEditing(null); setOpen(true); }}
          disabled={atCap || overCap}
          title={atCap || overCap ? "Plan staff limit reached — upgrade to add more." : ""}
          data-testid="add-staff-button"
        >
          <Plus className="h-4 w-4" /> New staff
        </Button>
      </div>

      {limitBanner && (
        <div
          className={`mt-4 rounded-[8px] border p-3 text-sm flex items-start gap-2 flex-wrap ${
            limitBanner.tone === "red"
              ? "bg-red-50 border-red-300 text-red-900"
              : "bg-amber-50 border-amber-300 text-amber-900"
          }`}
          data-testid={`staff-plan-limit-banner-${limitBanner.tone}`}
        >
          <span className="mono text-[10px] uppercase tracking-widest font-bold mt-0.5">
            {limitBanner.tone === "red" ? "Over cap" : "At cap"}
          </span>
          <span className="flex-1">{limitBanner.text}</span>
          {/* Phase P — inline Pro pricing on the upgrade CTA so users know
              the cost before clicking through. */}
          <span className="flex items-center gap-2 mt-0.5" data-testid="staff-plan-limit-banner-price">
            <PriceDisplay code="pro" size="sm" />
          </span>
        </div>
      )}

      <div className="mt-6">
        {loading ? (
          <div className="text-muted-foreground text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center text-muted-foreground text-sm">
            No staff yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((s) => {
              const fullUrl = `${origin}${s.staff_url}`;
              return (
                <div key={s.id} className="bg-card border border-border rounded-[8px] p-5" data-testid={`staff-card-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-base flex items-center gap-2">
                        {s.name}
                        {!s.active && <span className="mono text-[10px] uppercase tracking-widest bg-muted px-2 py-0.5 rounded">Inactive</span>}
                      </div>
                      <div className="mono text-xs text-muted-foreground mt-1 break-all">
                        <LinkIcon className="h-3 w-3 inline mr-1 -mt-0.5" />
                        {fullUrl}
                      </div>
                      {/* Phase 43 — base address summary on the card */}
                      {(s.base_address || "").trim() && (
                        <div className="text-[11px] mt-1.5 flex items-center gap-1.5 flex-wrap"
                             data-testid={`staff-base-summary-${s.id}`}>
                          <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                          <span className="text-muted-foreground truncate max-w-[260px]">{s.base_address}</span>
                          {s.base_lat != null && s.base_lng != null ? (
                            <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold"
                                  data-testid={`staff-base-ok-${s.id}`}>✓ Geocoded</span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-widest text-amber-600 font-bold"
                                  data-testid={`staff-base-warn-${s.id}`}>⚠ Drop a pin</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <SharePopover staff={s} fullUrl={fullUrl} />
                    <Button variant="outline" size="sm" onClick={() => copyUrl(fullUrl)} data-testid={`copy-staff-url-${s.id}`}>
                      <Copy className="h-3 w-3 mr-1" /> Copy URL
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditing(s); setOpen(true); }} data-testid={`edit-staff-${s.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    {(s.base_address || "").trim() && (s.base_lat == null) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPinningStaff(s)}
                        data-testid={`drop-pin-${s.id}`}
                        className="text-amber-700 border-amber-300 hover:bg-amber-50"
                      >
                        <MapPin className="h-3 w-3 mr-1" /> Drop pin
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-staff-${s.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{s.name}"?</AlertDialogTitle>
                          <AlertDialogDescription>This will permanently remove the staff member.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(s.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-staff-${s.id}`}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <StaffDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
      {/* Phase 43 — base-pin override flow */}
      <MapPinPicker
        open={!!pinningStaff}
        onOpenChange={(o) => { if (!o) setPinningStaff(null); }}
        initialQuery={pinningStaff?.base_address || ""}
        customerId={null}
        onPicked={async (p) => {
          if (!pinningStaff) return;
          try {
            await api.post(`/staff/${pinningStaff.id}/base-pin`, {
              lat: p.lat, lng: p.lng,
              base_address: p.formatted_address || pinningStaff.base_address || "",
            });
            toast.success(`Pin set for ${pinningStaff.name}`);
            await load();
          } catch (e) {
            toast.error(formatApiError(e, "Could not set pin"));
          } finally {
            setPinningStaff(null);
          }
        }}
      />
    </div>
  );
}

function SharePopover({ staff, fullUrl }) {
  const message = `Hi ${staff.name.split(" ")[0]}, here is your JobsBook portal link: ${fullUrl}\n\nYou'll need your 4-digit PIN to sign in.`;
  const subject = `Your JobsBook portal — ${staff.name}`;
  const enc = encodeURIComponent;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — copy from the URL field above");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`share-staff-${staff.id}`}>
          <Share2 className="h-3 w-3 mr-1" /> Share access
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" data-testid={`share-popover-${staff.id}`}>
        <div className="p-3 border-b border-border">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-accent">// Share access</div>
          <div className="text-xs text-muted-foreground mt-1">
            The PIN is <strong>not</strong> included — share it separately.
          </div>
        </div>
        <div className="divide-y divide-border">
          <button
            type="button"
            onClick={copyLink}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/60 text-left"
            data-testid={`share-copy-${staff.id}`}
          >
            <Copy className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Copy link</span>
          </button>
          <a
            href={`https://wa.me/?text=${enc(message)}`}
            target="_blank"
            rel="noreferrer"
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/60"
            data-testid={`share-whatsapp-${staff.id}`}
          >
            <MessageCircle className="h-4 w-4 text-emerald-600" />
            <span className="text-sm">WhatsApp</span>
          </a>
          <a
            href={`sms:?&body=${enc(message)}`}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/60"
            data-testid={`share-sms-${staff.id}`}
          >
            <Smartphone className="h-4 w-4 text-sky-600" />
            <span className="text-sm">SMS</span>
          </a>
          <a
            href={`mailto:?subject=${enc(subject)}&body=${enc(message)}`}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/60"
            data-testid={`share-email-${staff.id}`}
          >
            <Mail className="h-4 w-4 text-amber-600" />
            <span className="text-sm">Email</span>
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
